import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Home as HomeIcon, Key, Building, Calculator, ArrowRight, Star, CheckCircle, Heart, Search, Bed, PoundSterling, Loader2, Bath, MapPin } from 'lucide-react';
import { testimonials, stats, charityInfo } from '../mockData';
import { useLanguage } from '../context/LanguageContext';
import { useToast } from '../hooks/use-toast';
import { PropertyNews } from '../components/PropertyNews';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Home = () => {
  const { t, isArabic } = useLanguage();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Featured Properties from DB
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [loadingProperties, setLoadingProperties] = useState(true);

  useEffect(() => {
    const fetchFeaturedProperties = async () => {
      try {
        const response = await fetch(`${API_URL}/api/listings?status=active`);
        if (response.ok) {
          const data = await response.json();
          setFeaturedProperties(data.slice(0, 3)); // Get top 3
        }
      } catch (error) {
        console.error('Error fetching properties:', error);
      }
      setLoadingProperties(false);
    };
    fetchFeaturedProperties();
  }, []);
  
  // Property Seeker Quick Form State
  const [seekerForm, setSeekerForm] = useState({
    looking_for: '',
    property_type: '',
    bedrooms: '',
    max_budget: '',
    name: '',
    email: '',
    phone: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleQuickSearch = async (e) => {
    e.preventDefault();
    if (!seekerForm.looking_for || !seekerForm.name || !seekerForm.phone) {
      toast({ title: "Please fill required fields", variant: "destructive" });
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/property-seekers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...seekerForm,
          min_bedrooms: parseInt(seekerForm.bedrooms) || 1,
          max_bedrooms: parseInt(seekerForm.bedrooms) || 5,
          min_budget: 0,
          max_budget: parseFloat(seekerForm.max_budget) || 0,
          preferred_areas: []
        })
      });
      
      if (response.ok) {
        toast({ 
          title: isArabic ? "تم الإرسال!" : "Request Submitted!", 
          description: isArabic ? "سنتواصل معك قريباً" : "Our team will contact you shortly" 
        });
        setSeekerForm({ looking_for: '', property_type: '', bedrooms: '', max_budget: '', name: '', email: '', phone: '' });
      }
    } catch (error) {
      toast({ title: "Error", description: "Please try again", variant: "destructive" });
    }
    setSubmitting(false);
  };

  // Arabic stats labels
  const statsAr = [
    { value: '97%', label: 'من سعر الطلب المحقق' },
    { value: '92%', label: 'معدل تجديد المستأجرين' },
    { value: '500+', label: 'عقار تم بيعه' },
    { value: '15+', label: 'سنوات من الخبرة' }
  ];

  const displayStats = isArabic ? statsAr : stats;

  return (
    <div className="min-h-screen" dir={isArabic ? 'rtl' : 'ltr'}>
      {/* Hero Section */}
      <section className="relative h-[600px] lg:h-[700px] flex items-center hero-gradient">
        <div className="absolute inset-0 bg-black/20"></div>
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1600&q=80)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white z-10">
          <div className="max-w-3xl fade-in">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              {t('heroTitle')}
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-200">
              {isArabic ? 'نتائج ' : 'Results that '}<span className="text-[#FF8C42] font-semibold">{isArabic ? 'تحركك' : 'Move You'}</span>
            </p>
            <p className="text-lg mb-10 text-gray-300">
              {t('heroDescription')}
            </p>
            <div className={`flex flex-col sm:flex-row gap-4 ${isArabic ? 'sm:flex-row-reverse' : ''}`}>
              <Link to="/valuation">
                <Button size="lg" className="bg-[#FF8C42] hover:bg-[#FF6B35] text-white text-lg px-8 py-6 btn-hover-lift">
                  {t('getFreeValuation')}
                </Button>
              </Link>
              <Link to="/properties">
                <Button size="lg" variant="outline" className="bg-white text-[#404040] hover:bg-gray-100 text-lg px-8 py-6 border-2">
                  {t('browseProperties')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {displayStats.map((stat, index) => (
              <div key={index} className="text-center fade-in">
                <div className="stat-number mb-2">{stat.value}</div>
                <div className="text-gray-600 text-sm md:text-base">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#404040] mb-4">
              {t('whyWorkWithUs')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('comprehensiveServices')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="card-hover border-none shadow-lg">
              <CardContent className="p-6 text-center">
                <div className="feature-icon mx-auto">
                  <HomeIcon className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold text-[#404040] mb-3">
                  {isArabic ? 'بيع العقارات' : 'Property Sales'}
                </h3>
                <p className="text-gray-600 mb-4">
                  {isArabic ? 'إرشاد خبير لتحقيق 97% من السعر المطلوب' : 'Expert guidance achieving 97% of asking price'}
                </p>
                <Link to="/services" className={`text-[#FF8C42] hover:text-[#FF6B35] font-medium inline-flex items-center ${isArabic ? 'flex-row-reverse' : ''}`}>
                  {t('learnMore')} <ArrowRight className={`w-4 h-4 ${isArabic ? 'mr-1 rotate-180' : 'ml-1'}`} />
                </Link>
              </CardContent>
            </Card>

            <Card className="card-hover border-none shadow-lg">
              <CardContent className="p-6 text-center">
                <div className="feature-icon mx-auto">
                  <Key className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold text-[#404040] mb-3">
                  {isArabic ? 'تأجير العقارات' : 'Property Lettings'}
                </h3>
                <p className="text-gray-600 mb-4">
                  {isArabic ? 'معدل تجديد المستأجرين 92% مع دعم كامل' : '92% tenant renewal rate with full support'}
                </p>
                <Link to="/services" className={`text-[#FF8C42] hover:text-[#FF6B35] font-medium inline-flex items-center ${isArabic ? 'flex-row-reverse' : ''}`}>
                  {t('learnMore')} <ArrowRight className={`w-4 h-4 ${isArabic ? 'mr-1 rotate-180' : 'ml-1'}`} />
                </Link>
              </CardContent>
            </Card>

            <Card className="card-hover border-none shadow-lg">
              <CardContent className="p-6 text-center">
                <div className="feature-icon mx-auto">
                  <Building className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold text-[#404040] mb-3">
                  {isArabic ? 'إدارة العقارات' : 'Property Management'}
                </h3>
                <p className="text-gray-600 mb-4">
                  {isArabic ? 'خدمة إدارة كاملة لأصحاب العقارات' : 'Complete management service for landlords'}
                </p>
                <Link to="/services" className={`text-[#FF8C42] hover:text-[#FF6B35] font-medium inline-flex items-center ${isArabic ? 'flex-row-reverse' : ''}`}>
                  {t('learnMore')} <ArrowRight className={`w-4 h-4 ${isArabic ? 'mr-1 rotate-180' : 'ml-1'}`} />
                </Link>
              </CardContent>
            </Card>

            <Card className="card-hover border-none shadow-lg">
              <CardContent className="p-6 text-center">
                <div className="feature-icon mx-auto">
                  <Calculator className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold text-[#404040] mb-3">
                  {isArabic ? 'تقييمات الخبراء' : 'Expert Valuations'}
                </h3>
                <p className="text-gray-600 mb-4">
                  {isArabic ? 'تقييمات عقارية مجانية ودقيقة' : 'Free, accurate property valuations'}
                </p>
                <Link to="/valuation" className={`text-[#FF8C42] hover:text-[#FF6B35] font-medium inline-flex items-center ${isArabic ? 'flex-row-reverse' : ''}`}>
                  {t('getFreeValuation')} <ArrowRight className={`w-4 h-4 ${isArabic ? 'mr-1 rotate-180' : 'ml-1'}`} />
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Featured Properties */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`flex justify-between items-end mb-12 ${isArabic ? 'flex-row-reverse' : ''}`}>
            <div className={isArabic ? 'text-right' : ''}>
              <h2 className="text-3xl md:text-4xl font-bold text-[#404040] mb-4">
                {t('featuredProperties')}
              </h2>
              <p className="text-lg text-gray-600">
                {isArabic ? 'اكتشف منزل أحلامك في هارو' : 'Discover your dream home in Harrow'}
              </p>
            </div>
            <Link to="/properties">
              <Button variant="outline" className="hidden md:inline-flex border-[#FF8C42] text-[#FF8C42] hover:bg-[#FF8C42] hover:text-white">
                {t('viewAll')}
              </Button>
            </Link>
          </div>

          {loadingProperties ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#FF8C42]" />
            </div>
          ) : featuredProperties.length === 0 ? (
            <div className="text-center py-12">
              <Building className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-xl text-gray-500 mb-4">No properties available yet</p>
              <p className="text-gray-400 mb-6">Check back soon for new listings</p>
              <Link to="/landlord/login">
                <Button className="bg-[#FF8C42] hover:bg-[#FF6B35] text-white">
                  List Your Property
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredProperties.map((property) => (
                <Link to={`/property/${property.id}`} key={property.id}>
                  <Card className="property-card-hover overflow-hidden border-none shadow-lg h-full">
                    <div className="relative h-64 overflow-hidden">
                      <img
                        src={property.images?.[0] || property.image || 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=800&q=80'}
                        alt={property.title}
                        className="property-image w-full h-full object-cover"
                      />
                      <div className={`absolute top-4 ${isArabic ? 'left-4' : 'right-4'}`}>
                        <span className="bg-[#FF8C42] text-white px-3 py-1 rounded-full text-sm font-semibold">
                          {(property.listing_type || property.type) === 'sale' ? (isArabic ? 'للبيع' : 'For Sale') : (isArabic ? 'للإيجار' : 'To Rent')}
                        </span>
                      </div>
                      {property.images?.length > 1 && (
                        <div className="absolute bottom-2 right-2 bg-black/60 text-white px-2 py-1 rounded text-xs">
                          {property.images.length} photos
                        </div>
                      )}
                    </div>
                    <CardContent className="p-6">
                      <h3 className="text-xl font-bold text-[#404040] mb-2">{property.title}</h3>
                      <p className="text-gray-600 mb-4 flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {property.location || `${property.address}, ${property.city}`}
                      </p>
                      <div className="text-2xl font-bold text-[#FF8C42] mb-4">
                        £{(property.price || 0).toLocaleString()}{(property.listing_type || property.type) === 'rent' && (isArabic ? ' شهرياً' : ' pcm')}
                      </div>
                      <div className="flex justify-between text-sm text-gray-600 mb-4">
                        <span className="flex items-center gap-1"><Bed className="w-4 h-4" /> {property.bedrooms} {isArabic ? 'غرف' : 'Beds'}</span>
                        <span className="flex items-center gap-1"><Bath className="w-4 h-4" /> {property.bathrooms} {isArabic ? 'حمامات' : 'Baths'}</span>
                        <span>{property.floor_area || property.area || 0} {isArabic ? 'قدم²' : 'sq ft'}</span>
                      </div>
                      <Button className="w-full bg-[#404040] hover:bg-[#FF8C42] text-white">
                        {t('viewDetails')}
                      </Button>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}

          <div className="text-center mt-8 md:hidden">
            <Link to="/properties">
              <Button className="bg-[#FF8C42] hover:bg-[#FF6B35] text-white">
                {t('viewAll')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Charity Section */}
      <section className="section-padding bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${isArabic ? 'lg:grid-flow-dense' : ''}`}>
            <div className={isArabic ? 'lg:col-start-2' : ''}>
              <div className={`flex items-center mb-6 ${isArabic ? 'flex-row-reverse' : ''}`}>
                <Heart className={`w-12 h-12 text-[#FF8C42] ${isArabic ? 'ml-4' : 'mr-4'}`} />
                <div className={isArabic ? 'text-right' : ''}>
                  <h2 className="text-3xl md:text-4xl font-bold text-[#404040]">
                    {charityInfo.name}
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {isArabic ? 'مبادرة دعم المجتمع' : 'Community Support Initiative'}
                  </p>
                </div>
              </div>
              
              <p className={`text-lg text-gray-700 mb-6 leading-relaxed ${isArabic ? 'text-right' : ''}`}>
                {charityInfo.description}
              </p>

              <div className="bg-white p-6 rounded-lg shadow-md mb-6">
                <p className={`text-[#FF8C42] font-semibold italic text-lg ${isArabic ? 'text-right' : ''}`}>
                  &ldquo;{charityInfo.tagline}&rdquo;
                </p>
              </div>

              <div className="space-y-3 mb-8">
                {charityInfo.focus.map((item, index) => (
                  <div key={index} className={`flex items-start ${isArabic ? 'flex-row-reverse space-x-reverse' : ''} space-x-3`}>
                    <CheckCircle className="w-5 h-5 text-[#FF8C42] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>

              <Link to="/contact">
                <Button className="bg-[#FF8C42] hover:bg-[#FF6B35] text-white btn-hover-lift">
                  {isArabic ? 'اعرف المزيد عن أعمالنا الخيرية' : 'Learn More About Our Charity Work'}
                </Button>
              </Link>
            </div>

            <div className={`relative ${isArabic ? 'lg:col-start-1' : ''}`}>
              <img
                src="https://customer-assets.emergentagent.com/job_harry-and-jacobs/artifacts/ge8aro8t_BANNER.png"
                alt="H&J Charity"
                className="rounded-lg shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#404040] mb-4">
              {t('whatClientsSay')}
            </h2>
            <p className="text-lg text-gray-600">
              {isArabic ? 'لا تأخذ كلامنا فقط' : "Don't just take our word for it"}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.slice(0, 2).map((testimonial) => (
              <Card key={testimonial.id} className="testimonial-card p-6 border-none shadow-lg">
                <div className={`flex mb-4 ${isArabic ? 'justify-end' : ''}`}>
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-[#FF8C42] text-[#FF8C42]" />
                  ))}
                </div>
                <p className={`text-gray-700 mb-4 italic ${isArabic ? 'text-right' : ''}`}>
                  &ldquo;{testimonial.text}&rdquo;
                </p>
                <p className={`font-bold text-[#404040] ${isArabic ? 'text-right' : ''}`}>- {testimonial.name}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Property News Section */}
      <PropertyNews />

      {/* Property Search Form Section */}
      <section className="section-padding bg-gradient-to-br from-[#FF8C42] to-[#FF6B35] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <Search className="w-12 h-12 mx-auto mb-4 opacity-90" />
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {isArabic ? 'ابحث عن عقارك المثالي' : 'Find Your Perfect Property'}
            </h2>
            <p className="text-lg opacity-90">
              {isArabic 
                ? 'أخبرنا بما تبحث عنه وسنجده لك'
                : 'Tell us what you\'re looking for and we\'ll find it for you'}
            </p>
          </div>

          <form onSubmit={handleQuickSearch}>
            <Card className="bg-white/10 backdrop-blur-sm border-none shadow-2xl overflow-visible">
              <CardContent className="p-6 md:p-8 overflow-visible">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  {/* Looking for */}
                  <div className="relative">
                    <label className="text-sm opacity-80 mb-1 block">{isArabic ? 'نوع البحث *' : 'I want to *'}</label>
                    <select
                      value={seekerForm.looking_for}
                      onChange={(e) => setSeekerForm({...seekerForm, looking_for: e.target.value})}
                      className="w-full p-3 rounded-lg bg-white text-gray-800 border border-gray-300 cursor-pointer appearance-none relative z-10"
                      style={{ WebkitAppearance: 'menulist', MozAppearance: 'menulist' }}
                      required
                    >
                      <option value="">{isArabic ? 'اختر...' : 'Select...'}</option>
                      <option value="rent">{isArabic ? '🏠 إيجار' : '🏠 Rent'}</option>
                      <option value="sale">{isArabic ? '💰 شراء' : '💰 Buy'}</option>
                    </select>
                  </div>

                  {/* Property Type */}
                  <div className="relative">
                    <label className="text-sm opacity-80 mb-1 block">{isArabic ? 'نوع العقار' : 'Property Type'}</label>
                    <select
                      value={seekerForm.property_type}
                      onChange={(e) => setSeekerForm({...seekerForm, property_type: e.target.value})}
                      className="w-full p-3 rounded-lg bg-white text-gray-800 border border-gray-300 cursor-pointer appearance-none relative z-10"
                      style={{ WebkitAppearance: 'menulist', MozAppearance: 'menulist' }}
                    >
                      <option value="">{isArabic ? 'أي نوع' : 'Any'}</option>
                      <option value="flat">{isArabic ? 'شقة' : 'Flat'}</option>
                      <option value="house">{isArabic ? 'منزل' : 'House'}</option>
                      <option value="studio">{isArabic ? 'استوديو' : 'Studio'}</option>
                    </select>
                  </div>

                  {/* Bedrooms */}
                  <div className="relative">
                    <label className="text-sm opacity-80 mb-1 block">{isArabic ? 'غرف النوم' : 'Bedrooms'}</label>
                    <select
                      value={seekerForm.bedrooms}
                      onChange={(e) => setSeekerForm({...seekerForm, bedrooms: e.target.value})}
                      className="w-full p-3 rounded-lg bg-white text-gray-800 border border-gray-300 cursor-pointer appearance-none relative z-10"
                      style={{ WebkitAppearance: 'menulist', MozAppearance: 'menulist' }}
                    >
                      <option value="">{isArabic ? 'أي' : 'Any'}</option>
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4+</option>
                    </select>
                  </div>

                  {/* Budget */}
                  <div>
                    <label className="text-sm opacity-80 mb-1 block">
                      {isArabic ? 'الميزانية القصوى (£)' : 'Max Budget (£)'}
                    </label>
                    <Input
                      type="number"
                      value={seekerForm.max_budget}
                      onChange={(e) => setSeekerForm({...seekerForm, max_budget: e.target.value})}
                      placeholder={seekerForm.looking_for === 'rent' ? '2000' : '500000'}
                      className="w-full p-3 rounded-lg bg-white text-gray-800 border-none"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div>
                    <label className="text-sm opacity-80 mb-1 block">{isArabic ? 'الاسم *' : 'Name *'}</label>
                    <Input
                      value={seekerForm.name}
                      onChange={(e) => setSeekerForm({...seekerForm, name: e.target.value})}
                      placeholder={isArabic ? 'اسمك الكامل' : 'Your full name'}
                      className="w-full p-3 rounded-lg bg-white text-gray-800 border-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm opacity-80 mb-1 block">{isArabic ? 'البريد الإلكتروني' : 'Email'}</label>
                    <Input
                      type="email"
                      value={seekerForm.email}
                      onChange={(e) => setSeekerForm({...seekerForm, email: e.target.value})}
                      placeholder="your@email.com"
                      className="w-full p-3 rounded-lg bg-white text-gray-800 border-none"
                    />
                  </div>
                  <div>
                    <label className="text-sm opacity-80 mb-1 block">{isArabic ? 'رقم الهاتف *' : 'Phone *'}</label>
                    <Input
                      type="tel"
                      value={seekerForm.phone}
                      onChange={(e) => setSeekerForm({...seekerForm, phone: e.target.value})}
                      placeholder="07700 123456"
                      className="w-full p-3 rounded-lg bg-white text-gray-800 border-none"
                      required
                    />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    type="submit" 
                    disabled={submitting}
                    className="bg-white text-[#FF8C42] hover:bg-gray-100 px-8 py-6 text-lg font-semibold"
                  >
                    {submitting ? (
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    ) : (
                      <Search className="w-5 h-5 mr-2" />
                    )}
                    {isArabic ? 'ابحث لي' : 'Find My Property'}
                  </Button>
                  <Link to="/find-property">
                    <Button 
                      type="button"
                      variant="outline" 
                      className="border-2 border-white text-white hover:bg-white hover:text-[#FF8C42] px-8 py-6 text-lg"
                    >
                      {isArabic ? 'بحث متقدم' : 'Advanced Search'}
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </form>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#404040] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {t('readyToMove')}
          </h2>
          <p className="text-xl mb-8 text-gray-300">
            {isArabic 
              ? 'سواء كنت تشتري أو تبيع أو تستأجر، نحن هنا لمساعدتك في كل خطوة'
              : "Whether you're buying, selling, or renting, we're here to help every step of the way"
            }
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-[#FF8C42] hover:bg-[#FF6B35] text-white text-lg px-8 py-6 btn-hover-lift">
                {t('contactToday')}
              </Button>
            </Link>
            <Link to="/valuation">
              <Button size="lg" variant="outline" className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#404040] text-lg px-8 py-6">
                {t('getFreeValuation')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
